import type { FaqEntry } from '../utils/structuredData';
import { BUSINESS, PRICES, formatEuro } from '../config/business';
import { APP_CONFIG } from '../config/app';

const SCREEN_PRICE = PRICES.find((p) => p.id === 'ecran');
const SCREEN_PRICE_SENTENCE =
  SCREEN_PRICE?.priceFrom != null
    ? `Le remplacement d’écran d’iPhone coûte à partir de ${formatEuro(SCREEN_PRICE.priceFrom)} TTC en pièce compatible, selon le modèle.`
    : 'Le remplacement d’écran d’iPhone est sur devis.';

/** FAQ complète (page dépannage informatique). */
export const HOME_FAQ_ITEMS: FaqEntry[] = [
  {
    question: 'Quelles réparations d\'iPhone effectuez-vous ?',
    answer: 'Je remplace l\'écran, la batterie, la vitre arrière, le connecteur de charge et la caméra des iPhone, à domicile à Viry et autour de Genève. Le prix et le type de pièce vous sont communiqués avant toute intervention.'
  },
  {
    question: 'Combien de temps prend une réparation d\'iPhone ?',
    answer: 'Réponse en moins de 2h, intervention souvent le jour même. Une fois sur place, la plupart des réparations prennent entre 30 minutes et 1 journée, selon la panne.'
  },
  {
    question: 'Offrez-vous une garantie sur vos réparations ?',
    answer: 'Toutes mes réparations sont couvertes par une garantie 90 jours sur les pièces et la main-d\'œuvre. Problème non résolu = remboursé.'
  },
  {
    question: 'Intervenez-vous à domicile ?',
    answer: 'Oui, j\'interviens à domicile pour les particuliers et les entreprises autour de Genève (Pays de Gex, Haute-Savoie). Le déplacement est inclus dans le tarif, sans frais cachés.'
  },
  {
    question: 'Combien de temps prend une réparation d\'ordinateur ?',
    answer: 'La plupart des réparations basiques peuvent être effectuées en 24-48 heures. Les problèmes plus complexes peuvent prendre 3-5 jours ouvrables. Je vous donne toujours une estimation du délai avant de commencer.'
  },
  {
    question: 'Comment puis-je prendre rendez-vous pour une réparation ?',
    answer: 'Vous pouvez prendre rendez-vous par WhatsApp, par téléphone ou via le formulaire de contact en ligne. Pour les pannes urgentes, j\'interviens souvent le jour même.'
  },
  {
    question: 'Mon ordinateur est très lent, pouvez-vous l\'accélérer ?',
    answer: 'Oui, l\'optimisation des performances est l\'une de mes spécialités. J\'identifie les causes de la lenteur (logiciels malveillants, trop d\'applications au démarrage, disque dur saturé, mémoire insuffisante) et j\'applique des solutions adaptées. Dans la plupart des cas, je peux considérablement améliorer les performances de votre ordinateur sans nécessiter d\'investissement matériel supplémentaire.'
  },
  {
    question: 'Proposez-vous des services pour les TPE/PME ?',
    answer: 'Absolument. Je propose des services sur mesure pour les TPE/PME et indépendants : interventions sur site, maintenance et configuration des postes et du réseau, solutions cloud (Microsoft 365, Google Workspace), sécurité et sauvegardes. Contactez-moi pour un devis gratuit et sans engagement.'
  },
  {
    question: 'Pouvez-vous récupérer des données d\'un disque dur défaillant ?',
    answer: 'Dans la plupart des cas, oui. Mon taux de réussite pour la récupération de données est supérieur à 90% pour les disques qui n\'ont pas subi de dommages physiques. Pour les disques physiquement endommagés, je collabore avec des services de récupération spécialisés pour maximiser vos chances de récupérer vos données précieuses.'
  },
  {
    question: 'Quelles méthodes de paiement acceptez-vous ?',
    answer: 'J\'accepte toutes les principales cartes de crédit, PayPal, Apple Pay, Google Pay et les espèces. Pour les clients TPE/PME, je propose également des modalités de paiement adaptées.'
  },
];

/**
 * FAQ transverse de l'accueil (3 activités) — source unique pour l'affichage et le JSON-LD FAQPage.
 * Les réponses doivent rester cohérentes avec les engagements de la section « Pourquoi SOS IT Services ».
 */
export const HOME_FAQ_SHORT: FaqEntry[] = [
  {
    question: 'Intervenez-vous à domicile ?',
    answer: 'Oui. Je me déplace chez vous ou sur votre lieu de travail, à Viry et jusqu’à 50 km autour de Genève (Pays de Gex, Haute-Savoie). Le déplacement est inclus dans le prix annoncé.',
  },
  {
    question: 'Quels téléphones réparez-vous ?',
    answer: 'Les iPhone en priorité : écran, batterie, vitre arrière, connecteur de charge, caméra. Les smartphones Android (Samsung, Xiaomi…) sont réparés sur demande, selon la disponibilité des pièces.',
  },
  {
    question: 'Combien de temps prend une réparation ?',
    answer: 'Pour un téléphone, la réparation se fait souvent en moins d’1h sur place. Pour un ordinateur, la durée dépend du diagnostic : je vous l’annonce avec le devis, avant d’intervenir.',
  },
  {
    question: 'Quelle garantie offrez-vous ?',
    answer: 'Chaque intervention est couverte par une garantie 90 jours sur les pièces et la main-d’œuvre. Problème non résolu = remboursé.',
  },
  {
    question: 'Que comprend l’offre site web à 350 € + 80 €/mois ?',
    answer: 'Un site professionnel jusqu’à 10 pages, livré en 2 à 3 semaines : 350 € à la mise en ligne, puis 80 €/mois (engagement 12 mois) qui incluent l’hébergement, la maintenance et vos modifications.',
  },
  {
    question: 'Comment prendre rendez-vous ?',
    answer: `Par WhatsApp, par téléphone au ${BUSINESS.phoneDisplay} ou via le formulaire de contact. Réponse en moins de 2h, intervention souvent le jour même, 7j/7.`,
  },
  {
    question: 'Combien coûte un changement d’écran d’iPhone ?',
    answer: `${SCREEN_PRICE_SENTENCE} Le prix définitif vous est annoncé après un diagnostic gratuit, avant toute intervention. Toute la grille est sur la page tarifs.`,
    link: { to: APP_CONFIG.routes.reparationTelephoneTarifs, label: 'Voir tous les tarifs' },
  },
  {
    question: 'Réparez-vous les ordinateurs qui ne démarrent plus ?',
    answer: 'Oui, à domicile ou sur site. Je commence par un diagnostic gratuit pour trouver la cause, puis je vous annonce le prix avant d’intervenir (dépannage PC et Mac sur devis). Si le disque est touché, la récupération de données est aussi possible, sur devis.',
  },
  {
    question: 'Intervenez-vous en Suisse, à Genève ?',
    // TODO(Lucas): préciser les moyens de paiement côté Suisse (CHF, TWINT…) une fois confirmés
    answer: 'Oui. Basé à Viry, à 10 minutes de la frontière, j’interviens aussi côté Genève (Suisse), à domicile ou sur site. Les tarifs sont annoncés en euros TTC avant toute intervention.',
  },
];
